/**
 * Vite entry point for the AI Maturity Index portal SPA.
 *
 * The boot sequence is deliberately linear:
 *   1. Locate the `#app` root injected by `index.html`.
 *   2. Load `/config.json` — the same bundle serves authoring and
 *      published modes; the narrowing in `loadConfig` decides which.
 *   3. Build the correct data-source client (ApiClient in authoring,
 *      SnapshotClient in published).
 *   4. Mount the portal shell and kick off the initial refresh.
 *
 * Any failure between steps 1 and 4 renders a plaintext error banner into
 * the root so the analyst never sees a blank page. The specific error is
 * either the `ConfigLoadError.code` (fetch/parse/shape) or the load
 * failure surfaced through the shell's reactive store.
 */
import "./styles.css";

import { loadConfig, ConfigLoadError } from "./config";
import { bootDataSource, createPortal } from "./views/portal";

/**
 * Render a plaintext boot-failure banner. Kept intentionally dependency-
 * free so it works even before any child view is instantiated.
 */
function renderBootError(root: HTMLElement, message: string): void {
  root.replaceChildren();
  const banner = document.createElement("div");
  banner.className = "boot-error";
  banner.setAttribute("role", "alert");
  const heading = document.createElement("h1");
  heading.textContent = "The AI Maturity Index portal failed to start.";
  const body = document.createElement("p");
  body.textContent = message;
  banner.append(heading, body);
  root.append(banner);
}

async function main(): Promise<void> {
  const root = document.getElementById("app");
  if (root === null) {
    // No app root means the HTML template regressed. There's nowhere to
    // render an error, so fall back to `document.body` and continue.
    const fallback = document.body;
    fallback.innerHTML = "";
    renderBootError(
      fallback,
      "Missing #app root element in the page template.",
    );
    return;
  }

  let config;
  try {
    config = await loadConfig();
  } catch (cause) {
    const message =
      cause instanceof ConfigLoadError
        ? `${cause.code}: ${cause.message}`
        : cause instanceof Error
          ? cause.message
          : "Unknown error loading /config.json.";
    renderBootError(root, message);
    return;
  }

  const dataSource = bootDataSource(config);
  const portal = createPortal({ root, config, dataSource });
  // Fire-and-forget: refresh surfaces its own errors through the reactive
  // store, so we don't need to catch here.
  void portal.refresh();
}

void main();
